import React, { useEffect, useState } from "react";
import { Input, Label } from "reactstrap";
import Select from "react-select";
import {
  getStaffListApi,
  getOrganisationDepartmentListApi,
  getSecurityAuthRoleListApi,
} from "../../../helpers/fakebackend_helper";
import filterActiveStaff from "../staffActive";

// Change #100678 / #100679 - the vehicle check "Notifications" fields: when the
// reminder goes, who else gets it, and the escalation if the check is not
// done. Used by the Add/Edit Check popup (custom checks) and by the check
// schedules (Settings > Company Vehicle > Check Schedules).
//
// value    - the check (or schedule) row; ids are comma lists.
// onChange - called with a patch of the changed keys.
// idPrefix - keeps the checkbox ids unique when two copies are on a page.

// What a check schedule carries (repeat + the Notifications fields).
export const CHECK_SCHEDULE_KEYS = [
  "RepeatIntervalDays",
  "RepeatIntervalUnit",
  "ReminderLeadDays",
  "FlagRemindOnDueDate",
  "FlagNotifyDriver",
  "BackupStaffIds",
  "FlagEscalate",
  "EscalateAfterDays",
  "EscalateOrganisationDepartmentIds",
  "EscalateSecurityAuthRoleIds",
  "EscalateStaffIds",
];

const idsOf = (value) =>
  String(value ?? "")
    .split(",")
    .filter(Boolean);

// Change #100679 - the check fields a schedule sets (mirrors the backend's
// CompanyVehicleCheckLibrary::schedule_to_check).
export const scheduleToCheck = (s = {}) => ({
  RepeatIntervalDays:
    Number(s.RepeatIntervalDays) > 0 ? s.RepeatIntervalDays : "",
  RepeatIntervalUnit:
    Number(s.RepeatIntervalDays) > 0 ? s.RepeatIntervalUnit || null : null,
  ReminderLeadDays: s.ReminderLeadDays ?? "",
  FlagRemindOnDueDate: Number(s.FlagRemindOnDueDate) ? 1 : 0,
  FlagNotifyDriver: Number(s.FlagNotifyDriver) ? 1 : 0,
  BackupStaffIds: s.BackupStaffIds || "",
  BackupStaff_Id: idsOf(s.BackupStaffIds)[0] || "",
  FlagEscalate: Number(s.FlagEscalate) ? 1 : 0,
  EscalateAfterDays: s.EscalateAfterDays ?? "",
  EscalateOrganisationDepartmentIds: s.EscalateOrganisationDepartmentIds || "",
  EscalateOrganisationDepartment_Id:
    idsOf(s.EscalateOrganisationDepartmentIds)[0] || "",
  EscalateSecurityAuthRoleIds: s.EscalateSecurityAuthRoleIds || "",
  EscalateStaffIds: s.EscalateStaffIds || "",
});

// "Reminder 1 day(s) before and on the day · the driver · 2 backup person(s) · ..."
export const scheduleSummary = (s = {}) => {
  const lead =
    s.ReminderLeadDays === null ||
    s.ReminderLeadDays === undefined ||
    s.ReminderLeadDays === ""
      ? "Reminder at the company lead time"
      : Number(s.ReminderLeadDays) === 0
      ? "Reminder on the due date"
      : `Reminder ${s.ReminderLeadDays} day(s) before`;
  const parts = [
    lead +
      (Number(s.FlagRemindOnDueDate) && Number(s.ReminderLeadDays) !== 0
        ? " and on the day"
        : ""),
  ];
  if (Number(s.FlagNotifyDriver)) parts.push("the driver");
  const backups = idsOf(s.BackupStaffIds).length;
  if (backups) parts.push(`${backups} backup person(s)`);
  if (Number(s.FlagEscalate)) {
    parts.push(
      `escalates ${
        Number(s.EscalateAfterDays) > 0
          ? `${s.EscalateAfterDays} day(s) after`
          : "the day after"
      } the due date`
    );
  }
  return parts.join(" · ");
};
const portal = {
  menuPortalTarget: document.body,
  styles: { menuPortal: (base) => ({ ...base, zIndex: 2000 }) },
};

const CheckNotificationFields = ({
  value = {},
  onChange,
  idPrefix = "check",
  intro = "Fleet Managers and the vehicle's managers always get the reminder.",
  leadBlankHint = "Blank = the company default.",
}) => {
  const [staff, setStaff] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  // Days or weeks for the "remind before" box (stored as days).
  const [leadUnit, setLeadUnit] = useState(
    Number(value.ReminderLeadDays) > 0 &&
      Number(value.ReminderLeadDays) % 7 === 0
      ? "WEEK"
      : "DAY"
  );

  useEffect(() => {
    let active = true;
    getStaffListApi().then((res) => {
      if (active) setStaff(res?.staff_list || []);
    });
    getOrganisationDepartmentListApi().then((res) => {
      if (active) setDepartments(res?.organisationdepartment_list || []);
    });
    getSecurityAuthRoleListApi().then((res) => {
      if (active) setRoles(res?.securityauthrole_list || []);
    });
    return () => {
      active = false;
    };
  }, []);

  const backupIds = idsOf(value.BackupStaffIds || value.BackupStaff_Id);
  const departmentIds = idsOf(
    value.EscalateOrganisationDepartmentIds ||
      value.EscalateOrganisationDepartment_Id
  );
  const roleIds = idsOf(value.EscalateSecurityAuthRoleIds);
  const escalateIds = idsOf(value.EscalateStaffIds);

  // Active staff, plus whoever is already picked so a leaver still shows.
  const staffOptions = (keep) =>
    filterActiveStaff(staff || [], keep).map((st) => ({
      value: String(st.Id),
      label: st.FullName || `Staff #${st.Id}`,
    }));
  const departmentOptions = (departments || []).map((d) => ({
    value: String(d.Id),
    label: d.Name || `Department #${d.Id}`,
  }));
  const roleOptions = (roles || [])
    .filter(
      (r) =>
        r?.FlagActive == null ||
        String(r.FlagActive) === "1" ||
        roleIds.includes(String(r.Id))
    )
    .map((r) => ({ value: String(r.Id), label: r.Name || `Role #${r.Id}` }));

  const leadStep = leadUnit === "WEEK" ? 7 : 1;
  const leadCount =
    value.ReminderLeadDays === "" || value.ReminderLeadDays == null
      ? ""
      : Math.round(Number(value.ReminderLeadDays) / leadStep);
  const listOf = (list) => (list || []).map((o) => o.value);

  return (
    <>
      <div className="fw-semibold">Notifications</div>
      {intro && <small className="text-muted d-block mb-2">{intro}</small>}
      {/* "send the notification 4 days before and on the day" */}
      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
        <span>Send the reminder</span>
        <Input
          type="number"
          min={0}
          className="form-control form-control-sm"
          style={{ width: "70px" }}
          placeholder="-"
          value={leadCount}
          onChange={(e) =>
            onChange({
              ReminderLeadDays:
                e.target.value === ""
                  ? ""
                  : Math.max(0, Number(e.target.value)) * leadStep,
            })
          }
        />
        <Input
          type="select"
          className="form-select form-select-sm"
          style={{ width: "100px" }}
          value={leadUnit}
          onChange={(e) => {
            const step = e.target.value === "WEEK" ? 7 : 1;
            setLeadUnit(e.target.value);
            onChange({
              ReminderLeadDays: leadCount === "" ? "" : leadCount * step,
            });
          }}
        >
          <option value="DAY">day(s)</option>
          <option value="WEEK">week(s)</option>
        </Input>
        <span>before the due date</span>
      </div>
      <small className="text-muted d-block">
        0 = on the due date. {leadBlankHint}
      </small>
      <div className="form-check mb-2">
        <Input
          type="checkbox"
          className="form-check-input"
          id={`${idPrefix}RemindOnDueDate`}
          checked={!!Number(value.FlagRemindOnDueDate)}
          onChange={(e) =>
            onChange({ FlagRemindOnDueDate: e.target.checked ? 1 : 0 })
          }
        />
        <Label className="form-check-label" for={`${idPrefix}RemindOnDueDate`}>
          Send it again on the due date
        </Label>
      </div>
      <div className="form-check">
        <Input
          type="checkbox"
          className="form-check-input"
          id={`${idPrefix}NotifyDriver`}
          checked={!!Number(value.FlagNotifyDriver)}
          onChange={(e) =>
            onChange({ FlagNotifyDriver: e.target.checked ? 1 : 0 })
          }
        />
        <Label className="form-check-label" for={`${idPrefix}NotifyDriver`}>
          Notify whoever is driving the vehicle at the time
        </Label>
      </div>

      {/* The list column is the record; the first pick also goes in the old
          single column so the reminder still reaches someone before the
          list columns' delta has run. */}
      <Label className="mt-2 mb-1">Backup people</Label>
      <Select
        classNamePrefix="select2-selection"
        isMulti
        isLoading={staff === null}
        placeholder="People who are not the driver (optional)"
        options={staffOptions(backupIds)}
        value={staffOptions(backupIds).filter((o) =>
          backupIds.includes(o.value)
        )}
        onChange={(list) => {
          const ids = listOf(list);
          onChange({
            BackupStaffIds: ids.join(","),
            BackupStaff_Id: ids[0] || "",
          });
        }}
        {...portal}
      />
      <small className="text-muted">
        Always get the reminder too - covers a vehicle whose driver keeps
        changing or that is left unassigned.
      </small>

      <div className="form-check mt-3">
        <Input
          type="checkbox"
          className="form-check-input"
          id={`${idPrefix}Escalate`}
          checked={!!Number(value.FlagEscalate)}
          onChange={(e) => onChange({ FlagEscalate: e.target.checked ? 1 : 0 })}
        />
        <Label className="form-check-label" for={`${idPrefix}Escalate`}>
          Escalate if this check is not completed
        </Label>
      </div>
      {!!Number(value.FlagEscalate) && (
        <div className="ps-4 mt-1">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span>Escalate</span>
            <Input
              type="number"
              min={0}
              className="form-control form-control-sm"
              style={{ width: "70px" }}
              value={value.EscalateAfterDays ?? ""}
              onChange={(e) => onChange({ EscalateAfterDays: e.target.value })}
            />
            <span>day(s) after the due date</span>
          </div>
          <small className="text-muted d-block">
            0 or blank = the day after it falls due.
          </small>
          <Label className="mt-2 mb-1">To departments</Label>
          <Select
            classNamePrefix="select2-selection"
            isMulti
            placeholder="Everyone in these departments (optional)"
            options={departmentOptions}
            value={departmentOptions.filter((o) =>
              departmentIds.includes(o.value)
            )}
            onChange={(list) => {
              const ids = listOf(list);
              onChange({
                EscalateOrganisationDepartmentIds: ids.join(","),
                EscalateOrganisationDepartment_Id: ids[0] || "",
              });
            }}
            {...portal}
          />
          <Label className="mt-2 mb-1">And/or everyone with a role</Label>
          <Select
            classNamePrefix="select2-selection"
            isMulti
            placeholder="Security roles (optional)"
            options={roleOptions}
            value={roleOptions.filter((o) => roleIds.includes(o.value))}
            onChange={(list) =>
              onChange({ EscalateSecurityAuthRoleIds: listOf(list).join(",") })
            }
            {...portal}
          />
          <Label className="mt-2 mb-1">And/or these people</Label>
          <Select
            classNamePrefix="select2-selection"
            isMulti
            isLoading={staff === null}
            placeholder="Specific people (optional)"
            options={staffOptions(escalateIds)}
            value={staffOptions(escalateIds).filter((o) =>
              escalateIds.includes(o.value)
            )}
            onChange={(list) =>
              onChange({ EscalateStaffIds: listOf(list).join(",") })
            }
            {...portal}
          />
          <small className="text-muted">
            Sent once, with the backup people copied in.
          </small>
        </div>
      )}
    </>
  );
};

export default CheckNotificationFields;
